import { computed, ref, type ComputedRef, toValue } from "vue";

import type { UserMarketoInputName } from "../types/definitions/inputs/marketo-input-names-and-values.js";
import { CountryOptionKey } from "../locale/translation-keys/country-option-keys.js";
import { SiteCountryCode } from "../../types-and-definitions/locale-defs/Locales.js";
import { exhaustiveGuard, isNil, isNotNil, isObject, isString, type Nil } from "@mcwd/typescript-type-guards";
import { GdprCountryName } from "../../tracking/region/GdprCountries.js";
import { useEventListener } from "@vueuse/core";
import { getOneTrustValues } from "../../tracking/onetrust/getOneTrustValues.js";
import { type ILogger } from "../../utils/Logger/ILogger.js";
import type { RefOrGetter } from "@mcwd/util-vue";

const GdprCountry = GdprCountryName satisfies readonly CountryOptionKey[];
type GdprCountry = typeof GdprCountry[number];

function isGdprCountry(value: unknown): value is GdprCountry {
  return isString(value) && ((GdprCountry as readonly string[]).includes(value));
}

const GdprSiteLanguage = [ "uk", "fr", "de" ] as const satisfies readonly SiteCountryCode[];
type GdprSiteLanguage = (typeof GdprSiteLanguage[number]);

function isGdprSiteLanguage(value: unknown): value is GdprSiteLanguage {
  return isString(value) && ((GdprSiteLanguage as readonly string[]).includes(value));
}

type NoRuleSiteLanguage = Exclude<SiteCountryCode, GdprSiteLanguage>;
const NoRuleSiteLanguage = Object.freeze(SiteCountryCode.filter(lang => (isGdprSiteLanguage(lang) === false))) as readonly NoRuleSiteLanguage[];

function isNoRuleSiteLanguage(value: unknown): value is NoRuleSiteLanguage {
  return isString(value) && ((NoRuleSiteLanguage as readonly string[]).includes(value));
}

const PrivacyRule = ['gdpr'] as const;
type PrivacyRule = (typeof PrivacyRule)[number];

export class PrivacyConsentState<TEnabledFields extends string = string, TInputValues extends Record<TEnabledFields, any> = Record<TEnabledFields, any>> {

  constructor({ siteCountryCode, userInputValues, logger }: { siteCountryCode: SiteCountryCode, userInputValues: RefOrGetter<TInputValues>, logger: ILogger }) {
    this.#logger = logger;
    this.#userInputValues = userInputValues;

    this.#defs = Object.freeze({
      COUNTRY_MKTO_NAME: "Country",
      SITE_COUNTRY_CODE: siteCountryCode,
    });

    useEventListener(window, 'OneTrustGroupsUpdated', () => {
      this.#logger.debug("Event: OneTrustGroupsUpdated");
      this.#updateOneTrustGeoLocationData().catch(console.error);
    });
    this.#updateOneTrustGeoLocationData().catch(console.error);
  }

  async #updateOneTrustGeoLocationData() {
    const otValues = await getOneTrustValues();
    this.#logger.debug("Event: OneTrustGroupsUpdated", { otValues });
    if (isObject(otValues) && isObject(otValues.geolocation) && isString(otValues.geolocation.countryCode)) {
      this.#onetrustGeolocation.value = { countryCode: otValues.geolocation.countryCode, stateCode: otValues.geolocation.stateCode };
    }
    else {
      this.#onetrustGeolocation.value = null;
    }
  }

  #defs: Readonly<{
    COUNTRY_MKTO_NAME: UserMarketoInputName<"Country">;
    SITE_COUNTRY_CODE: SiteCountryCode;
  }>;

  #userInputValues: RefOrGetter<TInputValues>;
  #logger: ILogger;
  #country = computed<CountryOptionKey | Nil>(() => {
    if (isNil(this.#defs)) {
      return null;
    }
    const userValues: Record<string, any> | Nil = toValue(this.#userInputValues);
    if (this.#defs.COUNTRY_MKTO_NAME in userValues) {

      const country: CountryOptionKey | Nil = (userValues?.[this.#defs.COUNTRY_MKTO_NAME]) as CountryOptionKey | Nil;

      if (isString(country) && country.trim() !== "") {
        if (CountryOptionKey.includes(country) === false) {
          this.#logger.warn(`Unexpected value in Country field: "${country}"`);
        }
        return country;
      }
    }
    return null;
  });

  #onetrustGeolocation = ref<{ countryCode: string; stateCode?: string | Nil } | Nil>(null);

  #privacyRuleFromCountryInput = computed<PrivacyRule | Nil>(() => {
    if (isNotNil(this.#country.value)) {
      if (isGdprCountry(this.#country.value)) {
        return "gdpr";
      }
      else {
        return null;
      }
    }
    return null;
  });

  #privacyRuleFromSiteLang = computed<PrivacyRule | Nil>(() => {
    if (isString(this.#defs?.SITE_COUNTRY_CODE)) {
      if (isGdprSiteLanguage(this.#defs.SITE_COUNTRY_CODE)) {
        return "gdpr";
      }
      else if (isNoRuleSiteLanguage(this.#defs.SITE_COUNTRY_CODE)) {
        return null;
      }
      else {
        return exhaustiveGuard(this.#defs.SITE_COUNTRY_CODE);
      }
    }
    return null;
  });

  #privacyRuleFromOneTrust = computed<PrivacyRule | Nil>(() => {
    
    if (isObject(this.#onetrustGeolocation.value) && isString(this.#onetrustGeolocation.value.countryCode)) {
      if (this.#onetrustGeolocation.value.countryCode === "gb" || GdprSiteLanguage.includes(this.#onetrustGeolocation.value.countryCode as GdprSiteLanguage)) {
        return "gdpr";
      }
      else {
        return null;
      }
    }
    return null;
  });

  #activePrivacyRule = computed<PrivacyRule | Nil>(() => {
    
    const countryInputRule = this.#privacyRuleFromCountryInput.value;
    const onetrustRule = this.#privacyRuleFromOneTrust.value;
    const siteLangRule = this.#privacyRuleFromSiteLang.value;

    let activeRuleSource: "country input" | "onetrust geo" | "site locale" | Nil = null;
    if (isNotNil(countryInputRule)) activeRuleSource = "country input";
    else if (isNotNil(onetrustRule)) activeRuleSource = "onetrust geo";
    else if (isNotNil(siteLangRule)) activeRuleSource = "site locale";

    const activeRule = countryInputRule ?? onetrustRule ?? siteLangRule;

    this.#logger.debug("Active Privacy Rule: ", { countryInput: countryInputRule, onetrust: onetrustRule, siteLocale: siteLangRule, activeRule, activeRuleSource });

    return activeRule;
  });

  #consentRequired = computed<boolean>(() => {
    const activePrivacyRule = this.#activePrivacyRule.value;
    return (isNotNil(activePrivacyRule));
  });

  

  get activePrivacyRule(): ComputedRef<PrivacyRule | Nil> {
    return this.#activePrivacyRule;
  }

  get consentRequired(): ComputedRef<boolean> {
    return this.#consentRequired;
  }
}